import React, { useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Grid,
  Paper,
  Typography,
  InputAdornment,
  Alert,
  Divider,
  Tooltip,
  IconButton,
} from "@mui/material";
import {
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from "@mui/icons-material";
import { useForm, Controller, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { format } from "date-fns";
import { VENUES, type Venue } from "@restaurant-staff-portal/shared";
import { cashOutV2FormInputSchema } from "@/types/cashOutV2";
import { useBusinessConfig } from "@/hooks/useBusinessConfig";
import {
  useCashOutV2Calculations,
  formatCurrency,
  type CashOutV2FormValues,
} from "@/hooks/useCashOutV2Calculations";

// Form data type from schema
type CashOutV2FormData = CashOutV2FormValues & {
  entryDate: Date;
  venue: Venue;
  notes?: string;
};

interface CashOutFormV2Props {
  initialData?: Partial<CashOutV2FormData>;
  onSubmit: (data: CashOutV2FormData) => void;
  onCancel: () => void;
  isLoading?: boolean;
  venue: Venue;
}

// Currency input component
const CurrencyInput: React.FC<{
  field: any;
  label: string;
  error?: any;
  disabled?: boolean;
  helperText?: string;
  isEditMode?: boolean;
}> = ({ field, label, error, disabled = false, helperText, isEditMode = false }) => {
  const [localValue, setLocalValue] = React.useState<string>(() => {
    if (isEditMode && field.value !== undefined && field.value !== null) {
      return field.value.toFixed(2);
    } else if (!isEditMode && field.value !== undefined && field.value !== null && field.value !== 0) {
      return field.value.toFixed(2);
    }
    return "";
  });

  React.useEffect(() => {
    if (isEditMode && field.value !== undefined && field.value !== null) {
      setLocalValue(field.value.toFixed(2));
    } else if (!isEditMode && field.value !== undefined && field.value !== null && field.value !== 0) {
      setLocalValue(field.value.toFixed(2));
    } else if (!isEditMode && field.value === 0) {
      setLocalValue("");
    }
  }, [field.value, isEditMode]);

  return (
    <TextField
      {...field}
      value={localValue}
      label={label}
      type="number"
      fullWidth
      size="small"
      disabled={disabled}
      error={!!error}
      helperText={error?.message || helperText}
      placeholder="0.00"
      InputProps={{
        startAdornment: <InputAdornment position="start">$</InputAdornment>,
        inputProps: { step: 0.01, min: 0 },
      }}
      onChange={(e) => setLocalValue(e.target.value)}
      onBlur={() => {
        field.onBlur();
        if (localValue === "") {
          field.onChange(0);
          if (isEditMode) setLocalValue("0.00");
        } else {
          const numValue = parseFloat(localValue);
          if (!isNaN(numValue)) {
            const rounded = Math.round(numValue * 100) / 100;
            field.onChange(rounded);
            setLocalValue(rounded.toFixed(2));
          } else {
            if (field.value !== undefined && field.value !== null && field.value !== 0) {
              setLocalValue(field.value.toFixed(2));
            } else if (isEditMode) {
              setLocalValue("0.00");
            } else {
              setLocalValue("");
            }
          }
        }
      }}
    />
  );
};

// Currency field with Controller
const CurrencyField: React.FC<{
  name: keyof CashOutV2FormData;
  label: string;
  control: any;
  errors: any;
  disabled?: boolean;
  helperText?: string;
  isEditMode?: boolean;
}> = ({ name, label, control, errors, disabled = false, helperText, isEditMode = false }) => (
  <Controller
    name={name}
    control={control}
    render={({ field }) => (
      <CurrencyInput
        field={field}
        label={label}
        error={errors[name]}
        disabled={disabled}
        helperText={helperText}
        isEditMode={isEditMode}
      />
    )}
  />
);

// Calculated field display
const CalculatedField: React.FC<{
  label: string;
  value: number;
  helperText?: string;
}> = ({ label, value, helperText }) => (
  <TextField
    label={label}
    value={formatCurrency(value)}
    fullWidth
    size="small"
    disabled
    helperText={helperText}
    sx={{ "& .MuiInputBase-input.Mui-disabled": { WebkitTextFillColor: "#1976d2", fontWeight: 500 } }}
  />
);

// Section header
const SectionHeader: React.FC<{ title: string; info?: string; derived?: boolean }> = ({
  title,
  info,
  derived,
}) => (
  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
    <Typography variant="h6" sx={{ fontWeight: 600 }}>
      {title}
    </Typography>
    {derived && (
      <Typography variant="caption" sx={{ ml: 1, color: "text.secondary" }}>
        (Calculated)
      </Typography>
    )}
    {info && (
      <Tooltip title={info}>
        <IconButton size="small" sx={{ ml: 1 }}>
          <InfoIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    )}
  </Box>
);

// Reconciliation indicator
const ReconciliationIndicator: React.FC<{
  label: string;
  value: number;
  isBalanced: boolean;
  description: string;
}> = ({ label, value, isBalanced, description }) => (
  <Alert
    severity={isBalanced ? "success" : "error"}
    icon={isBalanced ? <CheckCircleIcon /> : <ErrorIcon />}
    sx={{ mb: 2 }}
  >
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
      <Box>
        <Typography variant="subtitle2" fontWeight="medium">
          {label}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {description}
        </Typography>
      </Box>
      <Typography variant="h6" fontWeight="bold">
        {formatCurrency(value)}
      </Typography>
    </Box>
  </Alert>
);

// Helper to convert Decimal to number
const decimalToNumber = (value: any): number => {
  if (value === null || value === undefined) return 0;
  if (typeof value === "object" && value !== null) {
    return Number(value.toString());
  }
  return Number(value) || 0;
};

export function CashOutFormV2({
  initialData,
  onSubmit,
  onCancel,
  isLoading,
  venue,
}: CashOutFormV2Props) {
  const { houseTipPercentage } = useBusinessConfig();
  const isEditMode = !!initialData;

  const entryDate = React.useMemo(() => {
    if (!initialData?.entryDate) return new Date();
    if (initialData.entryDate instanceof Date) return initialData.entryDate;
    return new Date(initialData.entryDate);
  }, [initialData?.entryDate]);

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<CashOutV2FormData>({
    resolver: zodResolver(cashOutV2FormInputSchema),
    defaultValues: {
      venue,
      entryDate,
      // Section 1: Toast Sales Data
      netSalesTotal: decimalToNumber(initialData?.netSalesTotal),
      totalGratuityAndFees: decimalToNumber(initialData?.totalGratuityAndFees),
      taxes: decimalToNumber(initialData?.taxes),
      giftCardSales: decimalToNumber(initialData?.giftCardSales),
      discounts: decimalToNumber(initialData?.discounts),
      totalNonCashTips: decimalToNumber(initialData?.totalNonCashTips),
      // Section 3: Account Info
      staffAccountAmount: decimalToNumber(initialData?.staffAccountAmount),
      staffAccountTip: decimalToNumber(initialData?.staffAccountTip),
      staffAccountGratuity: decimalToNumber(initialData?.staffAccountGratuity),
      houseAccountAmount: decimalToNumber(initialData?.houseAccountAmount),
      houseAccountTip: decimalToNumber(initialData?.houseAccountTip),
      houseAccountGratuity: decimalToNumber(initialData?.houseAccountGratuity),
      // Section 4: Payment Centres
      cashAmount: decimalToNumber(initialData?.cashAmount),
      cashTip: decimalToNumber(initialData?.cashTip),
      cashGratuity: decimalToNumber(initialData?.cashGratuity),
      creditAmount: decimalToNumber(initialData?.creditAmount),
      creditTip: decimalToNumber(initialData?.creditTip),
      creditGratuity: decimalToNumber(initialData?.creditGratuity),
      giftCardAmount: decimalToNumber(initialData?.giftCardAmount),
      giftCardTip: decimalToNumber(initialData?.giftCardTip),
      giftCardGratuity: decimalToNumber(initialData?.giftCardGratuity),
      notes: (initialData as any)?.notes || "",
    },
  });

  useEffect(() => {
    setValue("venue", venue);
  }, [venue, setValue]);

  // Watch all form values for real-time calculations
  const watchedValues = useWatch({ control }) as CashOutV2FormValues;

  // Calculate all derived fields
  const calculations = useCashOutV2Calculations(watchedValues, houseTipPercentage);

  return (
    <Paper elevation={0} sx={{ p: 3 }}>
      <form onSubmit={handleSubmit(onSubmit)}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            Cash Out Form V2 - {VENUES[venue as keyof typeof VENUES] || venue}
          </Typography>
          <Grid container spacing={2} sx={{ mt: 1, mb: 2 }}>
            <Grid item xs={12} sm={6} md={4}>
              <Controller
                name="entryDate"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Entry Date"
                    type="date"
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    value={field.value ? format(field.value, "yyyy-MM-dd") : ""}
                    onChange={(e) => {
                      const dateValue = e.target.value ? new Date(e.target.value) : null;
                      field.onChange(dateValue);
                    }}
                    error={!!errors.entryDate}
                    helperText={errors.entryDate?.message}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Section 1: Toast Sales Data */}
        <Box sx={{ mb: 4 }}>
          <SectionHeader title="Section 1: Toast Sales Data" info="Values from Toast POS reports" />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <CurrencyField name="netSalesTotal" label="Net Sales Total" control={control} errors={errors} isEditMode={isEditMode} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <CurrencyField name="totalGratuityAndFees" label="Total Gratuity & Fees" control={control} errors={errors} isEditMode={isEditMode} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <CurrencyField name="taxes" label="Taxes" control={control} errors={errors} isEditMode={isEditMode} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <CurrencyField name="giftCardSales" label="Gift Card Sales" control={control} errors={errors} isEditMode={isEditMode} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <CurrencyField name="discounts" label="Discounts" control={control} errors={errors} isEditMode={isEditMode} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <CurrencyField name="totalNonCashTips" label="Total Non-Cash Tips" control={control} errors={errors} isEditMode={isEditMode} />
            </Grid>
          </Grid>
        </Box>

        {/* Section 2: Calculated Sales Summary */}
        <Box sx={{ mb: 4, backgroundColor: "#f5f5f5", p: 2, borderRadius: 1 }}>
          <SectionHeader title="Section 2: Calculated Sales Summary" derived />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <CalculatedField
                label="Gross Sales"
                value={calculations.grossSales}
                helperText="Net Sales + Grat & Fees + Taxes + Discounts"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <CalculatedField
                label="Calculated Net Sales"
                value={calculations.calculatedNetSales}
                helperText="Gross Sales - Discounts"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <CalculatedField
                label="Complete Sales"
                value={calculations.completeSales}
                helperText="Calc Net Sales + Non-Cash Tips + Gift Cards"
              />
            </Grid>
          </Grid>
        </Box>

        {/* Section 3: Account Info */}
        <Box sx={{ mb: 4 }}>
          <SectionHeader title="Section 3: Account Info" />
          <Grid container spacing={3}>
            {/* Staff Account */}
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                  Staff Account
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <CurrencyField name="staffAccountAmount" label="Amount" control={control} errors={errors} isEditMode={isEditMode} />
                  </Grid>
                  <Grid item xs={4}>
                    <CurrencyField name="staffAccountTip" label="Tip" control={control} errors={errors} isEditMode={isEditMode} />
                  </Grid>
                  <Grid item xs={4}>
                    <CurrencyField name="staffAccountGratuity" label="Gratuity" control={control} errors={errors} isEditMode={isEditMode} />
                  </Grid>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <CalculatedField label="Staff Account Total" value={calculations.staffAccountTotal} />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
            {/* House Account */}
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                  House Account
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <CurrencyField name="houseAccountAmount" label="Amount" control={control} errors={errors} isEditMode={isEditMode} />
                  </Grid>
                  <Grid item xs={4}>
                    <CurrencyField name="houseAccountTip" label="Tip" control={control} errors={errors} isEditMode={isEditMode} />
                  </Grid>
                  <Grid item xs={4}>
                    <CurrencyField name="houseAccountGratuity" label="Gratuity" control={control} errors={errors} isEditMode={isEditMode} />
                  </Grid>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <CalculatedField label="House Account Total" value={calculations.houseAccountTotal} />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </Box>

        {/* Section 4: Payment Centres */}
        <Box sx={{ mb: 4 }}>
          <SectionHeader title="Section 4: Payment Centres" />
          <Grid container spacing={3}>
            {/* Cash */}
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                  Cash
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <CurrencyField name="cashAmount" label="Amount" control={control} errors={errors} isEditMode={isEditMode} />
                  </Grid>
                  <Grid item xs={4}>
                    <CurrencyField name="cashTip" label="Tip" control={control} errors={errors} isEditMode={isEditMode} />
                  </Grid>
                  <Grid item xs={4}>
                    <CurrencyField name="cashGratuity" label="Gratuity" control={control} errors={errors} isEditMode={isEditMode} />
                  </Grid>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <CalculatedField label="Cash Total" value={calculations.cashTotal} helperText="Amount + Tip + Gratuity" />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
            {/* Credit */}
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                  Credit
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <CurrencyField name="creditAmount" label="Amount" control={control} errors={errors} isEditMode={isEditMode} />
                  </Grid>
                  <Grid item xs={4}>
                    <CurrencyField name="creditTip" label="Tip" control={control} errors={errors} isEditMode={isEditMode} />
                  </Grid>
                  <Grid item xs={4}>
                    <CurrencyField name="creditGratuity" label="Gratuity" control={control} errors={errors} isEditMode={isEditMode} />
                  </Grid>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <CalculatedField label="Credit Total" value={calculations.creditTotal} />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
            {/* Gift Card */}
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                  Gift Card
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <CurrencyField name="giftCardAmount" label="Amount" control={control} errors={errors} isEditMode={isEditMode} />
                  </Grid>
                  <Grid item xs={4}>
                    <CurrencyField name="giftCardTip" label="Tip" control={control} errors={errors} isEditMode={isEditMode} />
                  </Grid>
                  <Grid item xs={4}>
                    <CurrencyField name="giftCardGratuity" label="Gratuity" control={control} errors={errors} isEditMode={isEditMode} />
                  </Grid>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <CalculatedField label="Gift Card Total" value={calculations.giftCardTotal} />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
            {/* House Accounts (Derived) */}
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2, backgroundColor: "#f5f5f5" }}>
                <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                  House Accounts <Typography component="span" variant="caption" color="text.secondary">(Derived from Section 3)</Typography>
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <CalculatedField label="Amount" value={calculations.houseAccountsAmount} />
                  </Grid>
                  <Grid item xs={4}>
                    <CalculatedField label="Tip" value={calculations.houseAccountsTip} />
                  </Grid>
                  <Grid item xs={4}>
                    <CalculatedField label="Gratuity" value={calculations.houseAccountsGratuity} />
                  </Grid>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <CalculatedField label="House Accounts Total" value={calculations.houseAccountsTotal} />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>

          {/* Payment Centre Totals */}
          <Box sx={{ mt: 3, backgroundColor: "#e3f2fd", p: 2, borderRadius: 1 }}>
            <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
              Payment Centre Totals
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <CalculatedField
                  label="Total Payment Centre"
                  value={calculations.totalPaymentCentre}
                  helperText="Cash + Credit + Gift Card + House Accounts"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <CalculatedField
                  label="Total Tips (Payment)"
                  value={calculations.totalTipsPayment}
                  helperText="Sum of all tips from Payment Centres"
                />
              </Grid>
            </Grid>
          </Box>
        </Box>

        {/* Section 5: Reconciliation */}
        <Box sx={{ mb: 4 }}>
          <SectionHeader title="Section 5: Reconciliation" derived />
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <ReconciliationIndicator
                label="Sales Reconciliation"
                value={calculations.salesReconciliation}
                isBalanced={calculations.reconciliationStatus.salesBalanced}
                description="Complete Sales - Total Payment Centre (should = $0.00)"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <ReconciliationIndicator
                label="Tips Reconciliation"
                value={calculations.tipsReconciliation}
                isBalanced={calculations.reconciliationStatus.tipsBalanced}
                description="Total Non-Cash Tips - Total Tips Payment (should = $0.00)"
              />
            </Grid>
          </Grid>
        </Box>

        {/* Section 6: House Calculations */}
        <Box sx={{ mb: 4, backgroundColor: "#f5f5f5", p: 2, borderRadius: 1 }}>
          <SectionHeader title="Section 6: House Calculations" derived />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <CalculatedField
                label="True Sales"
                value={calculations.trueSales}
                helperText="Calc Net Sales - Staff Acct Total - Total Grat & Fees"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <CalculatedField
                label={`House Tips (${(houseTipPercentage * 100).toFixed(0)}%)`}
                value={calculations.houseTips}
                helperText={`True Sales × ${(houseTipPercentage * 100).toFixed(0)}%`}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Section 7: Owed to Server/House */}
        <Box sx={{ mb: 4 }}>
          <SectionHeader title="Section 7: Owed to Server/House" derived />
          <Alert
            severity={calculations.owedDirection === "to_server" ? "success" : calculations.owedDirection === "to_house" ? "error" : "info"}
            sx={{ "& .MuiAlert-message": { width: "100%" } }}
          >
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
              <Box>
                <Typography variant="h6" fontWeight="bold">
                  {calculations.owedDirection === "to_server"
                    ? "Owed TO SERVER"
                    : calculations.owedDirection === "to_house"
                    ? "Owed TO HOUSE"
                    : "Balanced"}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  = (-Total Non-Cash Tips + House Tips) + Cash Total - Total Grat & Fees
                </Typography>
                <Typography variant="caption" display="block" color="text.secondary">
                  Positive = Server pays House | Negative = House pays Server
                </Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold">
                {formatCurrency(calculations.owedAmount)}
              </Typography>
            </Box>
          </Alert>
        </Box>

        {/* Form Actions */}
        <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
          <Button variant="outlined" onClick={onCancel} disabled={isLoading}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={isLoading}>
            {isLoading ? "Saving..." : "Submit Cash Out"}
          </Button>
        </Box>
      </form>
    </Paper>
  );
}
